import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import './StandardProductFilterSortModal.css';

const createDefaultFilters = () => ({
    width: { from: '', to: '' },
    length: { from: '', to: '' },
    gussetDim: { from: '', to: '' },
    packagingType: [],
    material: [],
    zipper: [],
    roundedCorners: [],
    hangHole: [],
    doubleCut: [],
    tearNotch: [],
    sealWidth: [],
    valve: [],
    gussetType: [],
});

const createDefaultSort = () => [];

function FilterCheckboxGroup({
    title,
    name,
    options = [],
    selectedValues = [],
    onChange,
    onAddSort,
    canSort = false
}) {
    const handleCheckboxChange = (event) => {
        const { value, checked } = event.target;
        onChange(name, value, checked);
    };

    return (
        <div className="filter-group">
            <div className="filter-group-header">
                <h4>{title}</h4>
                {canSort && options && options.length > 0 && (
                    <button type="button" onClick={() => onAddSort(name, title)} className="sort-add-button">Sort</button>
                )}
            </div>
            {options && options.length > 0 ? (
                <div className="filter-options-list">
                    {options.map(option => (
                        <label key={option.id}>
                            <input
                                type="checkbox"
                                name={name}
                                value={option.id}
                                checked={selectedValues.includes(String(option.id))}
                                onChange={handleCheckboxChange}
                            />
                            {option.name}
                        </label>
                    ))}
                </div>
            ) : (
                <div className="filter-options-list-empty">No options available.</div>
            )}
        </div>
    );
}

FilterCheckboxGroup.propTypes = {
    title: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
    })),
    selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    onAddSort: PropTypes.func.isRequired,
    canSort: PropTypes.bool,
};


function StandardProductFilterSortModal({
    isOpen,
    onClose,
    onSave,
    initialFilters = null,
    initialSort = [],
    filterOptions = {},
    sortableFields = [],
}) {
    const [filters, setFilters] = useState(() => initialFilters || createDefaultFilters());
    const [sortOrder, setSortOrder] = useState(() => initialSort || createDefaultSort());

    useEffect(() => {
        if (isOpen) {
            setFilters(initialFilters || createDefaultFilters());
            setSortOrder(initialSort || createDefaultSort());
        }
    }, [isOpen, initialFilters, initialSort]);

    const handleRangeChange = useCallback((event) => {
        const { name, value } = event.target;
        const [group, key] = name.split('.');
        setFilters(prevFilters => ({
            ...prevFilters,
            [group]: { ...prevFilters[group], [key]: value }
        }));
    }, []);

    const handleCheckboxChange = useCallback((groupName, value, isChecked) => {
        setFilters(prevFilters => {
            const currentSelection = prevFilters[groupName] || [];
            let newSelection;
            if (isChecked) {
                if (!currentSelection.includes(value)) {
                    newSelection = [...currentSelection, value];
                } else {
                    newSelection = currentSelection;
                }
            } else {
                newSelection = currentSelection.filter(item => item !== value);
            }
            return { ...prevFilters, [groupName]: newSelection };
        });
    }, []);

    const handleAddSort = useCallback((fieldName, fieldLabel) => {
        if (!sortOrder.find(sortItem => sortItem.id === fieldName)) {
            const sortableField = sortableFields.find(f => f.id === fieldName);
            const label = sortableField?.label || fieldLabel || fieldName;
            setSortOrder(prevOrder => [...prevOrder, { id: fieldName, label: label }]);
        }
    }, [sortOrder, sortableFields]);

    const handleRemoveSort = useCallback((fieldName) => {
        setSortOrder(prevOrder => prevOrder.filter(item => item.id !== fieldName));
    }, []);

    const handleSave = useCallback(() => {
        onSave(filters, sortOrder);
    }, [onSave, filters, sortOrder]);


    if (!isOpen) { return null; }
    const sortableFieldIds = sortableFields.map(f => f.id);
    const generateTitle = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Filter and Sort</h2>
                    <button onClick={onClose} className="close-button" aria-label="Close">×</button>
                </div>

                <div className="modal-body">
                    <div className="filter-panel">
                        <h3><i className="icon-filter"></i> Filter</h3>
                        <p className="panel-subtitle">Define the Filter</p>

                        <div className="filter-group dimension-group">
                            <h4>Width (in)</h4>
                            <div className="dimension-inputs-container">
                                <input type="number" name="width.from" placeholder="From" value={filters.width.from} onChange={handleRangeChange} />
                                <input type="number" name="width.to" placeholder="To" value={filters.width.to} onChange={handleRangeChange} />
                            </div>
                            {sortableFieldIds.includes('dimensions') && (
                                <button type="button" onClick={() => handleAddSort('dimensions', 'Dimensions')} className="sort-add-button dimension-sort-button">Sort</button>
                            )}
                        </div>
                        <div className="filter-group dimension-group">
                            <h4>Length (in)</h4>
                            <div className="dimension-inputs-container">
                                <input type="number" name="length.from" placeholder="From" value={filters.length.from} onChange={handleRangeChange} />
                                <input type="number" name="length.to" placeholder="To" value={filters.length.to} onChange={handleRangeChange} />
                            </div>
                        </div>
                        <div className="filter-group dimension-group">
                            <h4>Gusset (in)</h4>
                            <div className="dimension-inputs-container">
                                <input type="number" name="gussetDim.from" placeholder="From" value={filters.gussetDim.from} onChange={handleRangeChange} />
                                <input type="number" name="gussetDim.to" placeholder="To" value={filters.gussetDim.to} onChange={handleRangeChange} />
                            </div>
                        </div>

                        <div className="checkbox-filters-container">
                            {Object.keys(filterOptions).map(key => {
                                if (filters.hasOwnProperty(key)) {
                                    return (
                                        <FilterCheckboxGroup
                                            key={key}
                                            title={generateTitle(key)}
                                            name={key}
                                            id={filterOptions[key].id}
                                            options={filterOptions[key]}
                                            selectedValues={filters[key]}
                                            onChange={handleCheckboxChange}
                                            onAddSort={handleAddSort}
                                            canSort={sortableFieldIds.includes(key)}
                                        />
                                    );
                                }
                                return null;
                            })}
                        </div>
                    </div>

                    <div className="sort-panel">
                        <h3><i className="icon-sort"></i> Sort</h3>
                        <p className="panel-subtitle">Define the Sort</p>
                        <ul className="sort-list">
                            {sortOrder.map((sortItem) => (
                                <li key={sortItem.id}>
                                    <span>{sortItem.label}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSort(sortItem.id)}
                                        className="remove-sort-button"
                                        aria-label={`Remove ${sortItem.label} sort`}
                                    >
                                        <i className="icon-trash"></i>
                                    </button>
                                </li>
                            ))}
                            {sortOrder.length === 0 && <li className="empty-sort">No sort criteria defined.</li>}
                        </ul>
                    </div>
                </div>

                <div className="modal-footer">
                    <button type="button" onClick={onClose} className="button-cancel">Cancel</button>
                    <button type="button" onClick={handleSave} className="button-save">Save</button>
                </div>
            </div>
        </div>
    );
}

StandardProductFilterSortModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    initialFilters: PropTypes.object,
    initialSort: PropTypes.array,
    filterOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
    }))),
    sortableFields: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })),
};

export default StandardProductFilterSortModal;